import { computeScore } from '@core/score';
import { navigate } from '@ui/lib/router';
import { useStore } from '@ui/lib/useStore';

const SUB_META: {
  key: 'execution' | 'consistency' | 'energy' | 'learning';
  label: string;
  desc: string;
  color: string;
}[] = [
  {
    key: 'execution',
    label: 'EXECUÇÃO',
    desc: '% de sessões planejadas que você concluiu (últimos 14 dias)',
    color: 'var(--cyan)',
  },
  {
    key: 'consistency',
    label: 'CONSISTÊNCIA',
    desc: 'dias estudados vs. dias esperados (últimos 14 dias)',
    color: 'var(--purple)',
  },
  {
    key: 'energy',
    label: 'ENERGIA',
    desc: 'média do seu foco auto-relatado ao fim das sessões (últimos 7 dias)',
    color: 'var(--pink)',
  },
  {
    key: 'learning',
    label: 'APRENDIZADO',
    desc: 'retenção nas revisões espaçadas — responda na Home (últimos 14 dias)',
    color: 'var(--gold)',
  },
];

function ringColor(v: number | null): string {
  if (v === null) return 'var(--text-mute)';
  if (v >= 75) return 'var(--green)';
  if (v >= 50) return 'var(--cyan)';
  if (v >= 30) return 'var(--amber)';
  return 'var(--red)';
}

export function ScoreScreen() {
  const state = useStore((s) => s);
  const scoreLogs = state.scoreLogs;
  const result = computeScore(state);
  const { overall, subs, data_days } = result;

  const R = 60;
  const CIRC = 2 * Math.PI * R;

  // histórico últimos 30 dias
  const last30 = scoreLogs.slice(-30);
  const maxLog = 100;

  return (
    <div>
      <div class="main-head">
        <div>
          <h1 class="main-title">Performance Score</h1>
          <div class="main-sub">
            {data_days > 0 ? `calculado sobre ${data_days} dia(s) de dado real` : 'sem dado ainda'}
          </div>
        </div>
      </div>

      <div class="grid-2" style={{ marginBottom: '20px' }}>
        {/* Anel principal */}
        <div class="panel" style={{ textAlign: 'center', padding: '32px' }}>
          <div style={{ position: 'relative', width: '220px', height: '220px', margin: '0 auto' }}>
            <svg
              viewBox="0 0 160 160"
              style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}
            >
              <defs>
                <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stop-color="var(--cyan)" />
                  <stop offset="100%" stop-color="var(--purple)" />
                </linearGradient>
              </defs>
              <circle
                cx="80"
                cy="80"
                r={R}
                fill="none"
                stroke="rgba(0,240,255,0.08)"
                stroke-width="10"
              />
              {overall !== null && (
                <circle
                  cx="80"
                  cy="80"
                  r={R}
                  fill="none"
                  stroke="url(#scoreGrad)"
                  stroke-width="10"
                  stroke-linecap="round"
                  stroke-dasharray={CIRC}
                  stroke-dashoffset={CIRC * (1 - overall / 100)}
                  style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.2,.8,.2,1)' }}
                />
              )}
            </svg>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--f-heading)',
                  fontSize: 'var(--text-display)',
                  fontWeight: 900,
                  lineHeight: 1,
                  background: 'linear-gradient(180deg, var(--cyan), var(--purple))',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                {overall ?? '—'}
              </div>
              <div
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: '10px',
                  color: 'var(--text-dim)',
                  letterSpacing: '2px',
                  marginTop: '6px',
                }}
              >
                {overall !== null ? 'DE 100' : 'SEM DADO'}
              </div>
            </div>
          </div>

          {overall === null && (
            <div
              style={{
                marginTop: '20px',
                fontSize: '13.5px',
                color: 'var(--text-soft)',
                lineHeight: 1.6,
              }}
            >
              Seu score nasce com a primeira sessão de Pomodoro concluída.
              <br />
              <button
                class="btn primary sm"
                style={{ marginTop: '10px' }}
                onClick={() => navigate('pomodoro')}
              >
                ▶ COMEÇAR AGORA
              </button>
            </div>
          )}
          {overall !== null && data_days < 7 && (
            <div
              style={{
                marginTop: '18px',
                fontFamily: 'var(--f-mono)',
                fontSize: '11px',
                color: 'var(--amber)',
              }}
            >
              ⚠ score em calibração — fica confiável com 7+ dias de uso
            </div>
          )}
        </div>

        {/* Sub-scores */}
        <div class="panel">
          <div class="panel-title">
            ▰ <b>4 DIMENSÕES</b>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {SUB_META.map((m) => {
              const v = subs[m.key];
              return (
                <div key={m.key}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: '5px',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--f-heading)',
                        fontSize: '12px',
                        letterSpacing: '1.5px',
                        color: m.color,
                      }}
                    >
                      {m.label}
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--f-heading)',
                        fontSize: '18px',
                        fontWeight: 700,
                        color: ringColor(v),
                      }}
                    >
                      {v ?? '—'}
                    </span>
                  </div>
                  <div
                    style={{
                      height: '8px',
                      background: 'rgba(0,8,24,0.6)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      border: '1px solid var(--border-soft)',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${v ?? 0}%`,
                        background: `linear-gradient(90deg, ${m.color}, var(--purple))`,
                        transition: 'width 0.8s cubic-bezier(.2,.8,.2,1)',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--f-mono)',
                      fontSize: '10.5px',
                      color: 'var(--text-dim)',
                      marginTop: '4px',
                      lineHeight: 1.5,
                    }}
                  >
                    {m.desc}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Histórico */}
      <div class="panel">
        <div class="panel-title">
          ▰ <b>HISTÓRICO</b> · ÚLTIMOS 30 REGISTROS
        </div>
        {last30.length < 2 ? (
          <div class="empty-state" style={{ padding: '24px 0' }}>
            O histórico aparece a partir do 2º dia de uso. O score de cada dia é salvo
            automaticamente quando você abre a app.
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '4px',
              height: '140px',
              paddingTop: '10px',
            }}
          >
            {last30.map((log) => (
              <div
                key={log.date}
                title={`${log.date}: ${log.overall}`}
                style={{
                  flex: 1,
                  height: `${(log.overall / maxLog) * 100}%`,
                  minHeight: '3px',
                  background: 'linear-gradient(180deg, var(--cyan), rgba(0,240,255,0.15))',
                  borderRadius: '3px 3px 0 0',
                  boxShadow: '0 0 6px rgba(0,240,255,0.25)',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
